import type {
  ExportFileListItem,
  OperationListItem,
  OrderCsvPreview,
  OrderDetail,
  Paginated,
  UnifiedOrderListItem,
  UnifiedOrderListQuery,
} from '@flux/shared';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchOperationsOptions,
  fetchOrderCsvPreview,
  fetchOrderDetail,
  fetchOrderFiles,
  fetchUnifiedOrders,
} from './api.js';
import { queryKeys } from '@/lib/query/keys.js';

/**
 * Liste « Commandes » unifiée (`02` §3) : commandes Shopify et factures
 * Pennylane, tri/pagination/filtres pilotés par le serveur
 * (`README-shell.md`).
 */
export function useUnifiedOrdersList(
  query: UnifiedOrderListQuery,
): UseQueryResult<Paginated<UnifiedOrderListItem>> {
  return useQuery({
    queryKey: queryKeys.orders.unified(query),
    queryFn: () => fetchUnifiedOrders(query),
    placeholderData: keepPreviousData,
  });
}

/** `id` absent (route mal formée) : requête désactivée plutôt qu'un appel `/api/orders/` inutile. */
export function useOrderDetail(id: string | undefined): UseQueryResult<OrderDetail> {
  return useQuery({
    queryKey: queryKeys.orders.detail(id ?? ''),
    queryFn: () => fetchOrderDetail(id ?? ''),
    enabled: id !== undefined,
  });
}

/** Onglet CSV (`02` §4) : `fileId` absent = fichier courant de la commande. */
export function useOrderCsvPreview(id: string, fileId: string | undefined, enabled = true): UseQueryResult<OrderCsvPreview> {
  return useQuery({
    queryKey: queryKeys.orders.csv(id, fileId),
    queryFn: () => fetchOrderCsvPreview(id, fileId),
    enabled,
  });
}

/** Onglet Fichiers (`02` §4) : tous les CSV générés pour la commande, régénérations comprises. */
export function useOrderFiles(id: string): UseQueryResult<ExportFileListItem[]> {
  return useQuery({
    queryKey: queryKeys.orders.files(id),
    queryFn: () => fetchOrderFiles(id),
  });
}

/** Options du filtre « opération commerciale » (`02` §3). */
export function useOperationsOptions(): UseQueryResult<Paginated<OperationListItem>> {
  return useQuery({
    queryKey: queryKeys.orders.operationsOptions(),
    queryFn: fetchOperationsOptions,
    staleTime: 60_000,
  });
}
